import { formatExactMinorUnits } from '@norvix/utils';

/** Shared formatting for the financial report tabs (DEV-128/129). Money is exact: bigint over minor-unit strings. */

export const amountStyle = { whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' } as const;
export const MONTH = /^20\d{2}-(0[1-9]|1[0-2])$/;

/** Exact minor units → "1,234.56 EUR"; null (a missing rate) shows "Unavailable", never zero. */
export function money(value: string | null, currency: string): string {
  return value === null ? 'Unavailable' : `${formatExactMinorUnits(BigInt(value), currency, true)} ${currency}`;
}

/** Commission as a share of turnover, to two decimals, computed in integers. */
export function effectiveRate(fee: string | null, turnover: string | null): string {
  if (fee === null || turnover === null || BigInt(turnover) === 0n) return '—';
  const basis = (BigInt(fee) * 1_000_000n) / BigInt(turnover); // in 1/10,000 of a percent
  return `${(Number(basis) / 10_000).toFixed(2)}%`;
}

/** "2026-10" → "Oct 2026" (UTC). */
export function monthLabel(value: string): string {
  return new Date(`${value}-01T00:00:00Z`).toLocaleDateString('en-GB', { month: 'short', year: 'numeric', timeZone: 'UTC' });
}

export const DAY = /^20\d{2}-(0[1-9]|1[0-2])-(0[1-9]|[12]\d|3[01])$/;
/** Longest period a report covers: 36 calendar months, as the API enforces. */
export const MAX_PERIOD_MONTHS = 36;

/** Today's date in UTC, YYYY-MM-DD. */
export function todayUtc(now: Date = new Date()): string {
  return now.toISOString().slice(0, 10);
}

/** A real calendar day in YYYY-MM-DD form (rejects 2026-02-30). */
export function isDay(value: string): boolean {
  return DAY.test(value) && new Date(`${value}T00:00:00Z`).toISOString().slice(0, 10) === value;
}

/** Last calendar day (YYYY-MM-DD) of a YYYY-MM month. */
export function monthEnd(month: string): string {
  const [year, index] = month.split('-').map(Number) as [number, number];
  return new Date(Date.UTC(year, index, 0)).toISOString().slice(0, 10);
}

/** First day of the month `offset` months away from the month of `day`. */
export function monthStart(day: string, offset = 0): string {
  const [year, month] = day.split('-').map(Number) as [number, number];
  return new Date(Date.UTC(year, month - 1 + offset, 1)).toISOString().slice(0, 10);
}

/**
 * A `from`/`through` URL value as a day. Legacy links carry YYYY-MM: `from` becomes the 1st,
 * `through` the month's last day (or today for the current month). Anything else passes through.
 */
export function legacyFrom(value: string): string {
  return MONTH.test(value) ? `${value}-01` : value;
}
export function legacyThrough(value: string, today: string): string {
  if (!MONTH.test(value)) return value;
  const last = monthEnd(value);
  return last > today ? today : last;
}

/** Why a From/To pair cannot be reported, or null when it can. */
export function periodError(from: string, through: string, today: string): string | null {
  if (!isDay(from) || !isDay(through)) return 'Choose valid From and To dates.';
  if (from > through) return 'From must be on or before To.';
  if (through > today) return 'To cannot be after today (UTC).';
  const index = (day: string): number => Number(day.slice(0, 4)) * 12 + Number(day.slice(5, 7));
  if (index(through) - index(from) >= MAX_PERIOD_MONTHS) return `Choose a period of up to ${MAX_PERIOD_MONTHS} months.`;
  return null;
}

/** "2026-10-05" → "5 Oct 2026" (UTC). */
export function dayLabel(day: string): string {
  return new Date(`${day}T00:00:00Z`).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC' });
}

/** The selected period for captions: "1 Oct 2026 – 15 Oct 2026", or one day. */
export function periodLabel(from: string, through: string): string {
  return from === through ? dayLabel(from) : `${dayLabel(from)} – ${dayLabel(through)}`;
}

/** A monthly breakdown bucket: "Oct 2026" for a whole month, "01.10–15.10" for part of one. */
export function bucketLabel(bucket: { month: string; from: string; to: string; partial: boolean }): string {
  if (!bucket.partial) return monthLabel(bucket.month);
  const short = (day: string): string => `${day.slice(8, 10)}.${day.slice(5, 7)}`;
  return `${short(bucket.from)}–${short(bucket.to)}`;
}
